// オブジェクトの複製（貼り付け・ALT+ドラッグ複製）で共有する純粋ヘルパー。
//
// 既存オブジェクトから「サーバ管理フィールド（id / version / createdAt など）」を取り除き、
// dispatcher.createObjects へ渡せる draft へ変換する。pen-stroke は座標群を offset 分だけ
// 平行移動して相対形状を保つ。top-level の x / y は呼び出し側で確定位置を設定する想定。

import type { CanvasObject } from "@refinement-canvas/domain";
import type { CanvasObjectDraft } from "../features/canvas/use-canvas-op-dispatcher";

/**
 * 既存オブジェクトを複製用の draft へ変換する。
 *
 * @param object 複製元オブジェクト
 * @param offset pen-stroke の各点へ加算する平行移動量（通常は確定位置 - 元位置）。
 *   pen-stroke 以外では使用しない（top-level の x / y は呼び出し側で上書きする）。
 */
export function cloneObjectAsDraft(
  object: CanvasObject,
  offset: { readonly x: number; readonly y: number }
): CanvasObjectDraft {
  const {
    id: _id,
    version: _version,
    createdAt: _createdAt,
    createdBy: _createdBy,
    updatedAt: _updatedAt,
    updatedBy: _updatedBy,
    zOrder: _zOrder,
    ...draft
  } = object;
  if (draft.type === "pen-stroke") {
    return {
      ...draft,
      points: draft.points.map((point) => ({
        ...point,
        x: point.x + offset.x,
        y: point.y + offset.y,
      })),
    } as CanvasObjectDraft;
  }
  if (draft.type === "table") {
    // 構造 revision は「その表の形が何回変わったか」を表すサーバ管理の値であり、複製物は
    // 新しいオブジェクトとして 0 から数え直す（複製元の値を引き継ぐと、複製直後のセル更新が
    // 実在しない世代を指してしまう）。
    const { structureRevision: _structureRevision, ...tableDraft } = draft;
    return tableDraft as CanvasObjectDraft;
  }
  if (draft.type === "stamp") {
    // スタンプの作成者名スナップショット（createdByName）は createdBy と同じ「作成者」情報のため、
    // 複製物へは引き継がない。複製・貼り付けの作成者は操作した本人であり、作成時にサーバが本人の
    // 表示名で焼き込む（元スタンプの作成者名が確定まで一瞬バッジに残るのを防ぐ）。
    const { createdByName: _createdByName, ...stampDraft } = draft;
    return stampDraft as CanvasObjectDraft;
  }
  if (draft.type === "connector") {
    // 線の形・線のパターン・両端の形状・経路は複製物へ引き継ぐ（経路は始点アンカーからの相対値のため、
    // 両端と一緒に動かした複製でも同じ形になる）。競合検知の revision はサーバ管理の値で、複製物は
    // 新しいオブジェクトとして 0 から数え直す（サーバも作成時に 0 へ戻す）。
    const { connectorRevision: _connectorRevision, ...connectorDraft } = draft;
    return connectorDraft as CanvasObjectDraft;
  }
  return draft;
}
